"use client";
import { clientGet, clientPatch } from "@/service/client-api/client-api";
import { createFormFields } from "./model";
import { useContext, useEffect, useRef, useState } from "react";
import { useImmer } from "use-immer";
import { UserContext } from "@/contexts/user-context";
import { fromFormDefinitionToValidationSchema } from "@/forms/validation/validation-factory";
import { useForm } from "react-hook-form";
import { vestResolver } from "@hookform/resolvers/vest";
import { Form } from "@/components/ui/form";
import { MultiselectField } from "@/forms/fields/multiselect-field/multiselect-field";
import { toast } from "sonner";
import { logEvent } from "@/service/logs/log";
import { ResourceContext } from "@/contexts/resource-context";
import { BriefcaseIcon } from "lucide-react";

export function checkDoctorIsWithoutWorkplaces(
  keyType: string,
  dataForType: { workplaces?: string[] },
) {
  return (
    keyType === "doctor" &&
    (!dataForType?.workplaces || dataForType.workplaces.length === 0)
  );
}

export const DoctorPickWorkplacesHome = () => {
  const [fields, setFields] = useImmer<ReturnType<typeof createFormFields>>({});
  const { keyType, dataForType, setUser } = useContext(UserContext);
  const { logView } = useContext(ResourceContext);

  const form = useForm({
    resolver: vestResolver(fromFormDefinitionToValidationSchema(fields)),
  });
  const { handleSubmit } = form;

  const isDoctorWithoutWorkplaces = checkDoctorIsWithoutWorkplaces(
    keyType,
    dataForType,
  );

  const [isShown] = useState(isDoctorWithoutWorkplaces);

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedRef = useRef<string[]>(dataForType?.workplaces ?? []);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isDoctorWithoutWorkplaces) {
      return;
    }

    (async () => {
      const profileDataInfo = await clientGet("profile.data_info");
      setFields(createFormFields(profileDataInfo));
    })();
  }, [isDoctorWithoutWorkplaces]);

  if (!isDoctorWithoutWorkplaces && !isShown) {
    return null;
  }

  if (!fields.workplaces) {
    return null;
  }

  async function onSubmit() {
    return;
  }

  return (
    <div
      className={
        "from-remedium-blue/60 h-full rounded-lg border-2 border-transparent bg-gradient-to-br to-purple-600/60"
      }
    >
      <div
        className={
          "from-remedium-blue/5 flex h-full flex-col items-center justify-between gap-4 rounded-md bg-white bg-gradient-to-br to-purple-600/5 py-6 pr-2 pl-4 sm:flex-row sm:py-2"
        }
      >
        <div className={"flex flex-col items-center gap-4 sm:flex-row"}>
          <BriefcaseIcon className={"text-remedium-blue"} size={28} />
          <span className={"text-sm font-semibold"}>Gdzie pracujesz?</span>
        </div>
        <Form {...form}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className={"w-[208px] p-0"}
          >
            <MultiselectField
              field={fields.workplaces}
              shouldRenderLabel={false}
              onChange={(workplaces: string[]) => {
                setUser((draft) => {
                  draft.dataForType.workplaces = workplaces;
                });

                if (saveTimeoutRef.current) {
                  clearTimeout(saveTimeoutRef.current);
                }

                saveTimeoutRef.current = setTimeout(async () => {
                  const previous = lastSavedRef.current;
                  try {
                    await clientPatch(
                      "profile.update",
                      {},
                      {
                        dataForType: {
                          workplaces,
                        },
                      },
                    );
                    lastSavedRef.current = workplaces;
                    logEvent("doctor_pick_workplaces", logView, {
                      workplaces,
                      source: "home",
                    });
                    toast.success("Miejsce pracy zostało zapisane");
                  } catch {
                    setUser((draft) => {
                      draft.dataForType.workplaces = previous;
                    });
                    toast.error("Nie udało się zapisać miejsca pracy");
                  }
                }, 600);
              }}
            />
          </form>
        </Form>
      </div>
    </div>
  );
};
